/*
 * dfComponents core: the base class every component extends, a tiny event
 * emitter, DOM helpers and the component registry.
 *
 * Conventions shared by all components:
 *   new dfc.X(container, options)   mounts immediately into `container`
 *                                   (an element or a selector string)
 *   instance.update(partial)        merges options and re-renders in place
 *   instance.destroy()              removes DOM and listeners
 *   instance.on / off / once        events; handlers get one plain object
 *   instance.value                  getter/setter on value-bearing components
 *   instance.getValue() / setValue(v, { silent })
 *   'valueChanged'                  event { value, previous, source: 'user'|'api' }
 *                                   ('change' is emitted as well, with the same payload)
 *   options.value                   initial value
 * Styling comes only from CSS custom properties (see dfc.css); components
 * never set colours inline.
 */

export const VERSION = typeof __DFC_VERSION__ !== 'undefined' ? __DFC_VERSION__ : 'dev';

/** Resolves an element or selector; throws a readable error when missing. */
export function resolveElement(target, what) {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el || !(el instanceof Element)) {
		throw new Error(`dfc: ${what || 'component'} needs a container element${typeof target === 'string' ? ' (selector "' + target + '" matched nothing)' : ''}.`);
	}
	return el;
}

/** Escapes text for safe insertion into HTML. */
export function escapeHtml(value) {
	return String(value === undefined || value === null ? '' : value)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Creates an element. `attrs` keys: className, dataset (object), style
 * (object), on* handlers (onClick), text, html (trusted), anything else is
 * set as an attribute. `children` may be nodes, strings or arrays.
 */
export function h(tag, attrs = {}, ...children) {
	const el = document.createElement(tag);
	for (const [key, val] of Object.entries(attrs || {})) {
		if (val === undefined || val === null || val === false) continue;
		if (key === 'className') el.className = val;
		else if (key === 'dataset') Object.assign(el.dataset, val);
		else if (key === 'style' && typeof val === 'object') Object.assign(el.style, val);
		else if (key === 'text') el.textContent = val;
		else if (key === 'html') el.innerHTML = val;
		else if (key.startsWith('on') && typeof val === 'function') el.addEventListener(key.slice(2).toLowerCase(), val);
		else el.setAttribute(key, val === true ? '' : val);
	}
	const append = (child) => {
		if (child === undefined || child === null || child === false) return;
		if (Array.isArray(child)) child.forEach(append);
		else el.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
	};
	children.forEach(append);
	return el;
}

let idSeq = 0;
/** Unique id for ARIA wiring. */
export function uid(prefix = 'dfc') {
	idSeq += 1;
	return `${prefix}-${idSeq}`;
}

export function clamp(n, min, max) {
	return Math.min(max, Math.max(min, n));
}

/** Minimal event emitter mixed into every component. */
export class Emitter {
	constructor() { this._handlers = new Map(); }
	on(event, fn) {
		if (!this._handlers.has(event)) this._handlers.set(event, new Set());
		this._handlers.get(event).add(fn);
		return this;
	}
	off(event, fn) {
		const set = this._handlers.get(event);
		if (set) { if (fn) set.delete(fn); else set.clear(); }
		return this;
	}
	once(event, fn) {
		const wrapped = (payload) => { this.off(event, wrapped); fn(payload); };
		return this.on(event, wrapped);
	}
	emit(event, payload = {}) {
		const set = this._handlers.get(event);
		if (!set) return true;
		for (const fn of [...set]) {
			try { fn(payload); } catch (e) { console.error(`dfc: error in "${event}" handler`, e); }
		}
		return !payload.defaultPrevented;
	}
}

/**
 * Base class. Subclasses implement `render()` (build into this.el from
 * this.options) and may override `defaults()`, `bind()` (event wiring done
 * once) and `unbind()`.
 */
export class Component extends Emitter {
	static get componentName() { return 'component'; }

	constructor(container, options = {}) {
		super();
		// Components that declare `static get detachable() { return true; }` may be
		// built with a null container and placed later with mount().
		const detached = (container === null || container === undefined) && new.target.detachable;
		this.container = detached ? null : resolveElement(container, new.target.componentName);
		this.options = Object.assign({}, this.defaults(), options || {});
		this.id = uid(new.target.componentName);
		this.el = h('div', { className: `dfc dfc-${new.target.componentName}`, id: this.id });
		if (this.options.className) String(this.options.className).split(/\s+/).filter(Boolean).forEach((c) => this.el.classList.add(c));
		if (this.options.theme) this.el.setAttribute('data-theme', this.options.theme);
		if (this.container) this.container.appendChild(this.el);
		this._destroyed = false;
		this.render();
		this.bind();
	}

	static get detachable() { return false; }

	/**
	 * Moves the component into a container (an element or selector). Used after
	 * building a detachable component with a null container.
	 * @returns {this}
	 */
	mount(container) {
		if (this._destroyed) return this;
		this.container = resolveElement(container, this.constructor.componentName);
		this.container.appendChild(this.el);
		if (typeof this.mounted === 'function') this.mounted();
		return this;
	}

	/** Default options; subclasses extend. */
	defaults() { return {}; }

	/** Builds the DOM inside this.el. Must be idempotent (called on update). */
	render() {}

	/** One-time event wiring after the first render. */
	bind() {}

	/** Merge options and re-render. */
	update(partial = {}) {
		Object.assign(this.options, partial || {});
		if ('theme' in partial) {
			if (partial.theme) this.el.setAttribute('data-theme', partial.theme); else this.el.removeAttribute('data-theme');
		}
		this.render();
		this.emit('update', { options: this.options });
		return this;
	}

	/** Remove everything. Safe to call twice. */
	destroy() {
		if (this._destroyed) return;
		this._destroyed = true;
		this.unbind();
		this.emit('destroy', {});
		this._handlers.clear();
		if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
		this.el = null;
	}

	unbind() {}

	get destroyed() { return this._destroyed; }

	/** Value contract. Components that hold a value override `get value` / `set value`. */
	get value() { return undefined; }
	set value(v) { /* not a value-bearing component */ }
	getValue() { return this.value; }
	setValue(v, opts = {}) {
		if (opts && opts.silent) this._silentValue = true;
		try { this.value = v; } finally { this._silentValue = false; }
		return this;
	}
	/** Emits valueChanged (and change) unless the write was silent or the value is unchanged. */
	_valueChanged(value, previous, source = 'api', extra = {}) {
		if (this._silentValue) return false;
		if (Component._same(value, previous)) return false;
		const payload = Object.assign({ value, previous, source }, extra);
		this.emit('valueChanged', payload);
		this.emit('change', payload);
		return true;
	}
	static _same(a, b) {
		if (a === b) return true;
		if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => Component._same(x, b[i]));
		if (a && b && typeof a === 'object' && typeof b === 'object') { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } }
		return false;
	}
}

/** Registry so hosts can look components up by name (e.g. from a definition). */
const registry = new Map();
export function register(name, ctor) {
	registry.set(String(name).toLowerCase(), ctor);
	return ctor;
}
export function get(name) {
	return registry.get(String(name).toLowerCase()) || null;
}
export function list() {
	return [...registry.keys()];
}

/**
 * Field-type adapters let a host form engine render a component for a field
 * and read the value back, without knowing the component. Each adapter is
 * { create(container, field, value) -> instance, getValue(instance),
 *   setValue(instance, value) }.
 */
const fieldTypes = new Map();
export function registerFieldType(type, adapter) {
	fieldTypes.set(String(type).toLowerCase(), adapter);
	return adapter;
}
export function fieldType(type) {
	return fieldTypes.get(String(type).toLowerCase()) || null;
}

/** Sets the theme on an element (defaults to the document root). */
export function setTheme(theme, root = document.documentElement) {
	if (theme) root.setAttribute('data-theme', theme); else root.removeAttribute('data-theme');
}

/** Formats a timestamp (ms, ISO or Date) as { date, time } using the locale. */
export function formatWhen(at, locale) {
	const d = at instanceof Date ? at : new Date(at);
	if (isNaN(d.getTime())) return { date: '', time: '', iso: '' };
	return {
		date: d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }),
		time: d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
		iso: d.toISOString(),
		dayKey: d.toISOString().slice(0, 10),
	};
}
